/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { DateTime } from 'luxon'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import { RentalMandateTypes } from '../../enums/MandateTypes.mjs'
import { TRANSACTION_RENTAL } from '../../enums/TransactionCodes.mjs'
import { isManagedMandate } from '../../lib/common/mandate.mjs'
import { randomId } from '../../lib/random.mjs'
import { fetchEstate } from '../../lib/rest/estateAPIClient'
import { patchLease, postLease } from '../../lib/rest/leaseAPIClient'
import { fetchMandate } from '../../lib/rest/mandateAPIClient'
import LeaseSchema from '../../schemas/LeaseSchema.mjs'
import AgencyFeesField from '../fields/AgencyFeesField'
import ChargesField from '../fields/ChargesField'
import CustomerField from '../fields/CustomerField'
import DurationField from '../fields/DurationField'
import EndDateField from '../fields/EndDateField'
import EquippedField from '../fields/EquippedField'
import EstateField from '../fields/EstateField'
import FurnishedField from '../fields/FurnishedField'
import ManagedField from '../fields/ManagedField'
import NoticeDateField from '../fields/NoticeDateField'
import PaymentDayField from '../fields/PaymentDayField'
import PriceField from '../fields/PriceField'
import RenewalDateField from '../fields/RenewalDateField'
import SecurityDepositField from '../fields/SecurityDepositField'
import StartDateField from '../fields/StartDateField'
import TaxIncludedField from '../fields/TaxIncludedField'
import FormSection from '../FormSection'
import UseEstateInfoModal from './UseEstateInfoModal'
import UseMandateInfoModal from './UseMandateInfoModal'
import { useQuery } from '@tanstack/react-query'
import LeaseTemplateField from '../fields/LeaseTemplateField'
import {
  DOCUMENT_ENTRY_INVENTORY,
  DOCUMENT_EXIT_INVENTORY,
  DOCUMENT_LEASE,
  DOCUMENT_NOTICE
} from '../../enums/LeaseDocumentTypeCodes.mjs'
import MandateField from '../fields/MandateField'
import LeaseFileInput from '../inputs/LeaseFileInput'
import { queryClient } from '../../../../client/js/lib/query'
import { getLeaseInvoiceListLink, getLeaseListLink } from '../../lib/links/leaseLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { Group, SimpleGrid, Stack } from '@mantine/core'
import { fetchLeaseTemplate } from '../../lib/rest/leaseTemplateAPIClient'
import DurationUnitField from '../fields/DurationUnitField'
import FormDateTimeField from '../../../../client/js/components/form/fields/FormDatetimeField'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'

export type LeaseFormProps = {
  initialValues: Partial<Lease>
}

function LeaseForm (props: LeaseFormProps) {
  const { initialValues } = props
  const intl = useIntl()
  const auth = useContext(AuthContext)
  const id = initialValues?.id
  const editable = auth.hasPermission('write:lease')

  const [createdId, setCreatedId] = useState()

  const form = useCustomForm({
    disabled: !editable,
    initialValues: useMemo<Partial<Lease>>(() => ({
      isAgencyFeesWithTax: false,
      isManagementFeesWithTax: false,
      tmpRef: randomId(),
      ...initialValues
    }), [initialValues]),
    initializeField: createFieldInitializer(LeaseSchema),
    validate: createFormValidator(LeaseSchema),
    validateField: createFieldValidator(LeaseSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, LeaseSchema)
      return id > 0
        ? patchLease(id, finalValues)
        : postLease(finalValues)
    },
    onSuccess (result) {
      if (result.data.id && initialValues?.id == null) {
        setCreatedId(result.data.id)
      }
      queryClient.invalidateQueries({ queryKey: ['lease', id] })
      queryClient.invalidateQueries({ queryKey: ['leases'] })
    },
    transform (mutation, values) {
      // Update end date when notice date is modified.
      if (values.endDate == null && values.noticeDate != null && values.noticeDuration != null) {
        const endDateTime = DateTime.fromISO(values.noticeDate)
          .plus({ [values.noticeDurationUnit]: values.noticeDuration })
          .endOf('day')

        if (endDateTime.isValid && endDateTime.year >= 2000 &&
          endDateTime > DateTime.fromISO(values.noticeDate)) {
          mutation.endDate = endDateTime.toISO()
        }
      }

      // Update renewal date when start date is modified.
      if (values.startDate != null && values.duration != null && values.durationUnit != null) {
        let renewalDateTime = DateTime.fromISO(values.startDate)
          .plus({ [values.durationUnit]: values.duration })
          .endOf('day')

        if (renewalDateTime.isValid && renewalDateTime.year > 2000) {
          let maxRenewalDateTime = DateTime.now()
          const noticeDateTime = DateTime.fromISO(values.noticeDate)
          const endDateTime = DateTime.fromISO(values.endDate)

          if (endDateTime.isValid && endDateTime.year > 2000) {
            maxRenewalDateTime = endDateTime
          } else if (noticeDateTime.isValid && noticeDateTime.year > 2000) {
            maxRenewalDateTime = noticeDateTime
          }

          while (renewalDateTime <= maxRenewalDateTime) {
            renewalDateTime = renewalDateTime
              .plus({ [values.durationUnit]: values.duration })
              .endOf('day')
          }
          mutation.renewalDate = renewalDateTime.toISO()
        }
      }
      return mutation
    }
  })
  const { setValues, values } = form

  // Estate.
  const estateQuery = useQuery({
    enabled: values.estateId != null,
    queryKey: ['estate', values.estateId],
    queryFn: () => fetchEstate(values.estateId)
  })
  const { data: estate } = estateQuery

  const [isApplyEstateDataModalOpen, setIsApplyEstateDataModalOpen] = useState<boolean>(false)

  const handleCancelApplyEstateData = useCallback(() => {
    setIsApplyEstateDataModalOpen(false)
  }, [])

  const handleConfirmApplyEstateData = useCallback(() => {
    setIsApplyEstateDataModalOpen(false)

    if (estate) {
      setValues({
        equipped: estate.equipped,
        furnished: estate.furnished,
        LeaseCharges: (estate.Charges || [])
          .map(({ id: chargeId, ...charge }) => charge)
      }, { partial: true })
    }
  }, [estate, setValues])

  // Update form values based on estate.
  useEffect(() => {
    if (values.estateId > 0 && initialValues.estateId !== values.estateId) {
      setIsApplyEstateDataModalOpen(true)
    }
  }, [initialValues.estateId, values.estateId])

  // Mandate.
  const mandateQuery = useQuery({
    enabled: values.mandateId != null,
    queryKey: ['mandate', values.mandateId],
    queryFn: () => fetchMandate(values.mandateId)
  })
  const { data: mandate } = mandateQuery

  const [isApplyMandateDataModalOpen, setIsApplyMandateDataModalOpen] = useState(false)

  const handleCancelApplyMandateData = useCallback(() => {
    setIsApplyMandateDataModalOpen(false)
  }, [])

  const handleConfirmApplyMandateData = useCallback(() => {
    setIsApplyMandateDataModalOpen(false)

    if (mandate) {
      setValues({
        managed: isManagedMandate(mandate),
        agencyFees: mandate.agencyFees,
        isAgencyFeesWithTax: mandate.isAgencyFeesWithTax,
        managementFees: mandate.managementFees,
        isManagementFeesWithTax: mandate.isManagementFeesWithTax,
        rent: mandate.price,
        securityDeposit: mandate.securityDeposit
      }, { partial: true })
    }
  }, [mandate, setValues])

  // Update form values based on mandate.
  useEffect(() => {
    if (values.mandateId > 0 && initialValues.mandateId !== values.mandateId) {
      setIsApplyMandateDataModalOpen(true)
    }
  }, [initialValues.mandateId, values.mandateId])

  const leaseTemplateFilter = useMemo(() => ({
    furnished: values.furnished,
    managed: values.managed
  }), [values.furnished, values.managed])

  // Lease Template.
  const leaseTemplateQuery = useQuery({
    enabled: values.templateId > 0,
    queryKey: ['leaseTemplate', values.templateId],
    queryFn: () => fetchLeaseTemplate(values.templateId),
    refetchOnWindowFocus: false
  })
  const { data: leaseTemplate } = leaseTemplateQuery

  // Update form values based on template.
  useEffect(() => {
    if (values.templateId > 0 &&
      initialValues.templateId !== values.templateId &&
      leaseTemplate != null &&
      initialValues.id == null) {
      setValues({
        duration: leaseTemplate.duration,
        durationUnit: leaseTemplate.durationUnit,
        noticeDuration: leaseTemplate.noticeDuration,
        noticeDurationUnit: leaseTemplate.noticeDurationUnit
      }, { partial: true })
    }
  }, [initialValues.id, initialValues.templateId, leaseTemplate, setValues, values.templateId])

  const mandateFilters = useMemo(() => ({
    customerId: values.lessorId,
    estateId: values.estateId,
    type: RentalMandateTypes
  }), [values.estateId, values.lessorId])

  const agencyFeesPercentage = useMemo(() => (
    values.agencyFees != null && values.rent != null
      ? (values.agencyFees * 100) / values.rent
      : null
  ), [values.agencyFees, values.rent])

  const managementFeesPercentage = useMemo(() => (
    values.managementFees != null && values.rent != null
      ? (values.managementFees * 100) / values.rent
      : null
  ), [values.managementFees, values.rent])

  const estateFilter = useMemo(() => ({
    ownerId: values.lessorId,
    transaction: TRANSACTION_RENTAL
  }), [values.lessorId])

  const lessorFilter = useMemo(() => ({ isOwner: true }), [])

  return (
    <SmartForm
      context={form}
      redirectUrl={createdId != null
        ? getLeaseInvoiceListLink(createdId)
        : (id == null ? getLeaseListLink() : undefined)
      }
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <SimpleGrid cols={{ lg: 2 }}>
          <FormSection
            // disabled={!!id}
            defaultOpened
            title={intl.formatMessage({
              id: 'general',
              defaultMessage: 'Général'
            })}
          >
            <CustomerField
              disabled={!!id}
              name="tenantId"
              label={intl.formatMessage({
                id: 'tenant',
                defaultMessage: 'Locataire'
              })}
            />
            <CustomerField
              disabled={!!id}
              queryFilter={lessorFilter}
              label={intl.formatMessage({
                id: 'lessor',
                defaultMessage: 'Bailleur'
              })}
              name="lessorId"
            />
            <EstateField
              disabled={initialValues.estateId != null}
              queryFilter={estateFilter}
            />
            <MandateField queryFilter={mandateFilters} />
          </FormSection>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'characteristics',
              defaultMessage: 'Caractéristiques'
            })}
          >
            <SimpleGrid cols={{ lg: 3 }}>
              <ManagedField disabled={id != null} />
              <FurnishedField disabled={!!id} />
              <EquippedField disabled={!!id} />
            </SimpleGrid>
            <LeaseTemplateField
              disabled={id > 0}
              queryFilter={leaseTemplateFilter}
            />
            <SimpleGrid cols={{ lg: 2 }}>
              <Group wrap="nowrap" align="end">
                <DurationField
                  disabled={!!id}
                  label={intl.formatMessage({
                    id: 'lease_duration',
                    defaultMessage: 'Durée du bail'
                  })}
                  name="duration"
                />
                <DurationUnitField
                  disabled={!!id}
                  name="durationUnit"
                />
              </Group>
              <Group wrap="nowrap" align="end">
                <DurationField
                  disabled={!!id}
                  label={intl.formatMessage({
                    id: 'notice_duration',
                    defaultMessage: 'Durée du préavis'
                  })}
                  name="noticeDuration"
                />
                <DurationUnitField
                  disabled={!!id}
                  name="noticeDurationUnit"
                />
              </Group>
            </SimpleGrid>
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'lease_start_and_end',
              defaultMessage: 'Début et fin du bail'
            })}
          >
            <SimpleGrid cols={{ lg: 2 }}>
              <StartDateField
                disabled={!!id}
                label={intl.formatMessage({
                  id: 'entry_date',
                  defaultMessage: 'Date d\'entrée'
                })}
                description={<FormattedMessage
                  id="lease_start_date_help"
                  defaultMessage="Indiquez la date d'entrée des lieux (début du bail)."
                />}
              />
              <RenewalDateField
                disabled
                min={values.startDate}
                description={<FormattedMessage
                  id="lease_renewal_date_help"
                  defaultMessage="Date anniversaire du contrat."
                />}
              />
            </SimpleGrid>
            <SimpleGrid cols={{ lg: 2 }}>
              <NoticeDateField
                min={values.startDate}
                max={values.endDate}
                description={<FormattedMessage
                  id="lease_notice_date_help"
                  defaultMessage="Indiquez la date du préavis du locataire."
                />}
              />
              <EndDateField
                label={intl.formatMessage({
                  id: 'release_date',
                  defaultMessage: 'Date de sortie'
                })}
                min={values.noticeDate}
                description={<FormattedMessage
                  id="lease_end_date_help"
                  defaultMessage="Indiquez la date de sortie des lieux."
                />}
              />
            </SimpleGrid>
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'inventory',
              defaultMessage: 'État des lieux'
            })}
          >
            <SimpleGrid cols={{ lg: 2 }}>
              <FormDateTimeField
                label={intl.formatMessage({
                  id: 'entry_inventory_date',
                  defaultMessage: 'Date de l\'état des lieux d\'entrée'
                })}
                name="entryInventoryDate"
                description={<FormattedMessage
                  id="entry_inventory_date_help"
                  defaultMessage="Laisser vide si la date correspond au début du bail."
                />}
              />
              <FormDateTimeField
                disabled={values.endDate == null}
                label={intl.formatMessage({
                  id: 'exit_inventory_date',
                  defaultMessage: 'Date de l\'état des lieux de sortie'
                })}
                name="exitInventoryDate"
                description={<FormattedMessage
                  id="exit_inventory_date_help"
                  defaultMessage="Laisser vide si la date correspond à la fin du bail."
                />}
              />
            </SimpleGrid>
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'billing',
              defaultMessage: 'Facturation'
            })}
          >
            <SimpleGrid cols={{ lg: 2 }}>
              <FormDateTimeField
                min={values.startDate}
                placeholder={values.startDate}
                label={intl.formatMessage({
                  id: 'billing_start_date',
                  defaultMessage: 'Date de début de facturation'
                })}
                name="billingStartDate"
                description={<FormattedMessage
                  id="lease_billing_start_date_help"
                  defaultMessage="Aucune facture ne sera générée avant cette date."
                />}
              />
              <FormDateTimeField
                max={values.endDate}
                placeholder={values.endDate}
                label={intl.formatMessage({
                  id: 'billing_end_date',
                  defaultMessage: 'Date de fin de facturation'
                })}
                name="billingEndDate"
                description={<FormattedMessage
                  id="lease_billing_end_date_help"
                  defaultMessage="Aucune facture ne sera générée après cette date."
                />}
              />
            </SimpleGrid>
          </FormSection>
        </SimpleGrid>

        <SimpleGrid cols={{ lg: 2 }}>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'rent_and_guaranties',
              defaultMessage: 'Loyer et garanties'
            })}
          >
            <SimpleGrid cols={{ lg: 2 }}>
              <PriceField
                label={intl.formatMessage({
                  id: 'rent_excluding_charges',
                  defaultMessage: 'Loyer hors charges'
                })}
                name="rent"
              />
              <PaymentDayField disabled={!!id} />
            </SimpleGrid>

            <SimpleGrid cols={{ lg: 2 }}>
              <SecurityDepositField
                disabled={initialValues?.id != null}
                label={intl.formatMessage({
                  id: 'security_deposit',
                  defaultMessage: 'Dépôt de garantie'
                })}
              />
            </SimpleGrid>
          </FormSection>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'fees',
              defaultMessage: 'Honoraires'
            })}
          >
            <SimpleGrid cols={{ lg: 2 }}>
              <AgencyFeesField
                amount={values.agencyFees}
                total={values.rent}
                label={intl.formatMessage({
                  id: 'agency_fees',
                  defaultMessage: 'Honoraires de location ({percentage}%)'
                }, {
                  percentage: agencyFeesPercentage != null
                    ? intl.formatNumber(
                      agencyFeesPercentage,
                      { maximumFractionDigits: 4 }
                    )
                    : '?'
                })}
              />
              <TaxIncludedField name="isAgencyFeesWithTax" />
            </SimpleGrid>
            <SimpleGrid cols={{ lg: 2 }}>
              <AgencyFeesField
                disabled={!values.managed}
                amount={values.managementFees}
                total={values.rent}
                label={intl.formatMessage({
                  id: 'management_fees',
                  defaultMessage: 'Honoraires de gestion ({percentage}%)'
                }, {
                  percentage: managementFeesPercentage != null
                    ? intl.formatNumber(
                      managementFeesPercentage,
                      { maximumFractionDigits: 4 }
                    )
                    : '?'
                })}
                name="managementFees"
                required={values.managed}
              />
              <TaxIncludedField
                disabled={!values.managed}
                name="isManagementFeesWithTax"
              />
            </SimpleGrid>
          </FormSection>
        </SimpleGrid>

        <FormSection
          defaultOpened={id == null || values.LeaseCharges?.length > 0}
          title={intl.formatMessage({
            id: 'periodic_charges',
            defaultMessage: 'Charges périodiques'
          }) + ' - ' + (values.LeaseCharges?.length || 0)}
        >
          <ChargesField name="LeaseCharges" />
        </FormSection>

        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'files',
            defaultMessage: 'Fichiers'
          })}
        >
          <SimpleGrid cols={{ lg: 2 }}>
            <LeaseFileInput
              documentType={DOCUMENT_LEASE}
              label={intl.formatMessage({
                id: 'lease_file',
                defaultMessage: 'Bail de location'
              })}
              leaseId={id}
              tmpRef={values.tmpRef}
              required={values.startDate != null && DateTime.fromISO(values.startDate).toMillis() <= Date.now()}
            />
            <LeaseFileInput
              documentType={DOCUMENT_NOTICE}
              label={intl.formatMessage({
                id: 'rental_notice',
                defaultMessage: 'Préavis'
              })}
              leaseId={id}
              required={values.noticeDate != null && DateTime.fromISO(values.noticeDate).toMillis() <= Date.now()}
              tmpRef={values.tmpRef}
            />
            <LeaseFileInput
              documentType={DOCUMENT_ENTRY_INVENTORY}
              label={intl.formatMessage({
                id: 'entry_inventory',
                defaultMessage: 'État des lieux d\'entrée'
              })}
              leaseId={id}
              tmpRef={values.tmpRef}
              required={(values.startDate != null && DateTime.fromISO(values.startDate).toMillis() <= Date.now()) ||
                (values.entryInventoryDate != null && DateTime.fromISO(values.entryInventoryDate).toMillis() <= Date.now())}
            />
            <LeaseFileInput
              documentType={DOCUMENT_EXIT_INVENTORY}
              label={intl.formatMessage({
                id: 'exit_inventory',
                defaultMessage: 'État des lieux de sortie'
              })}
              leaseId={id}
              tmpRef={values.tmpRef}
              required={(values.endDate != null && DateTime.fromISO(values.endDate).toMillis() <= Date.now()) ||
                (values.exitInventoryDate != null && DateTime.fromISO(values.exitInventoryDate).toMillis() <= Date.now())}
            />
          </SimpleGrid>
        </FormSection>

        <ButtonGroup>
          {editable
            ? <>
              <FormResetButton />
              <FormSaveButton />
            </>
            : null}
        </ButtonGroup>

        <UseEstateInfoModal
          opened={isApplyEstateDataModalOpen}
          onClose={handleCancelApplyEstateData}
          onConfirm={handleConfirmApplyEstateData}
        />

        <UseMandateInfoModal
          opened={isApplyMandateDataModalOpen}
          onClose={handleCancelApplyMandateData}
          onConfirm={handleConfirmApplyMandateData}
        />
      </Stack>
    </SmartForm>
  )
}

export default LeaseForm
